<template>
  <Teleport to="body">
    <!-- Overlay -->
    <Transition name="overlay-fade">
      <div
        v-if="isOpen"
        class="search-drawer-overlay"
        @click="$emit('close')"
        aria-hidden="true"
      ></div>
    </Transition>

    <!-- Drawer -->
    <Transition name="drawer-slide">
      <div
        v-if="isOpen"
        class="search-drawer"
        role="dialog"
        aria-modal="true"
        aria-label="Buscar productos"
        tabindex="-1"
        ref="drawerRef"
      >
        <!-- Header -->
        <div class="search-drawer-header">
          <h5 class="search-drawer-title">Buscar</h5>
          <button
            class="search-drawer-close"
            @click="$emit('close')"
            aria-label="Cerrar búsqueda"
            type="button"
          >
            <i class="fas fa-times"></i>
          </button>
        </div>

        <!-- Search input -->
        <div class="search-drawer-input-wrap">
          <div class="search-input-inner">
            <i class="fas fa-search search-input-icon"></i>
            <input
              ref="inputRef"
              type="text"
              class="search-drawer-input"
              placeholder="¿Qué estás buscando?"
              v-model="query"
              @input="onInput"
              @keydown.enter.prevent="goToResults"
              @keydown.escape="$emit('close')"
              autocomplete="off"
              spellcheck="false"
            />
            <button
              v-if="query.length > 0"
              class="search-clear-btn"
              type="button"
              @click="clearQuery"
              aria-label="Limpiar búsqueda"
            >
              <i class="fas fa-times-circle"></i>
            </button>
          </div>
        </div>

        <!-- Body -->
        <div class="search-drawer-body">
          <!-- Loading skeleton -->
          <template v-if="isSearching">
            <div class="search-skeleton-item" v-for="n in 4" :key="n">
              <div class="search-skeleton-img"></div>
              <div class="search-skeleton-text">
                <div class="search-skeleton-name"></div>
                <div class="search-skeleton-price"></div>
              </div>
            </div>
          </template>

          <!-- Results -->
          <template v-else-if="results.length > 0">
            <div class="search-results-header">
              <span class="search-results-count">{{ results.length }} resultado{{ results.length !== 1 ? 's' : '' }}</span>
              <button class="search-see-all-btn" @click="goToResults" type="button">
                Ver todos <i class="fas fa-arrow-right ms-1"></i>
              </button>
            </div>
            <a
              v-for="product in results"
              :key="product.id"
              :href="`/producto/${product.id}`"
              class="search-result-item"
              @click.prevent="onProductClick(product)"
            >
              <div class="search-result-img-wrap">
                <img
                  v-if="product.image"
                  :src="product.image"
                  :alt="product.name"
                  class="search-result-img"
                  loading="lazy"
                />
                <div v-else class="search-result-img-placeholder">
                  <i class="fas fa-box"></i>
                </div>
              </div>
              <div class="search-result-info">
                <p class="search-result-name">{{ product.name }}</p>
                <p class="search-result-brand" v-if="product.brand">{{ product.brand }}</p>
                <div class="search-result-price-row">
                  <span
                    v-if="product.total_discount > 0 && product.price"
                    class="search-result-original-price"
                  >{{ formatPrice(product.price, product.currency) }}</span>
                  <span class="search-result-price">{{ formatPrice(product.final_price || product.price, product.currency) }}</span>
                  <span v-if="product.total_discount > 0" class="search-result-badge">
                    {{ Math.round(product.total_discount) }}% OFF
                  </span>
                </div>
              </div>
              <i class="fas fa-chevron-right search-result-arrow"></i>
            </a>
          </template>

          <!-- No results -->
          <div v-else-if="query.length >= 2 && !isSearching" class="search-empty-state">
            <i class="fas fa-search search-empty-icon"></i>
            <p class="search-empty-title">Sin resultados</p>
            <p class="search-empty-sub">No encontramos productos para <strong>"{{ query }}"</strong></p>
            <a href="/productos" class="btn btn-outline-secondary btn-sm mt-3" @click="$emit('close')">
              Ver catálogo completo
            </a>
          </div>

          <!-- Initial state (no query) -->
          <div v-else-if="query.length === 0" class="search-initial-state">
            <i class="fas fa-search search-initial-icon"></i>
            <p class="search-initial-text">Escribí para buscar productos</p>
          </div>
        </div>

        <!-- Footer: go to full results -->
        <div v-if="results.length > 0 && !isSearching" class="search-drawer-footer">
          <button class="search-full-results-btn" @click="goToResults" type="button">
            Ver todos los resultados para "{{ query }}"
          </button>
        </div>
      </div>
    </Transition>
  </Teleport>
</template>

<script setup>
import { ref, watch, nextTick, onMounted, onUnmounted, defineProps, defineEmits } from 'vue';
import { useRouter } from 'vue-router';
import { getSearchSuggestions } from '@/services/products';

const props = defineProps({
  isOpen: {
    type: Boolean,
    default: false,
  },
});

const emit = defineEmits(['close']);

const router = useRouter();
const drawerRef = ref(null);
const inputRef = ref(null);

const query = ref('');
const results = ref([]);
const isSearching = ref(false);
let searchTimeout = null;

const formatPrice = (price, currency) => {
  if (!price && price !== 0) return '';
  if (currency === 'USD') return `USD ${Number(price).toFixed(2)}`;
  return new Intl.NumberFormat('es-AR', {
    style: 'currency',
    currency: 'ARS',
    maximumFractionDigits: 0,
  }).format(price);
};

const onInput = () => {
  if (searchTimeout) clearTimeout(searchTimeout);

  if (query.value.length < 2) {
    results.value = [];
    isSearching.value = false;
    return;
  }

  isSearching.value = true;

  searchTimeout = setTimeout(async () => {
    try {
      const data = await getSearchSuggestions(query.value);
      results.value = data.results || [];
    } catch {
      results.value = [];
    } finally {
      isSearching.value = false;
    }
  }, 250);
};

const clearQuery = () => {
  query.value = '';
  results.value = [];
  isSearching.value = false;
  inputRef.value?.focus();
};

const goToResults = () => {
  if (!query.value.trim()) return;
  router.push({ path: '/productos', query: { search: query.value.trim() } });
  emit('close');
};

const onProductClick = (product) => {
  router.push(`/producto/${product.id}`);
  emit('close');
};

const handleKeydown = (e) => {
  if (e.key === 'Escape' && props.isOpen) emit('close');
};

onMounted(() => window.addEventListener('keydown', handleKeydown));
onUnmounted(() => {
  window.removeEventListener('keydown', handleKeydown);
  if (searchTimeout) clearTimeout(searchTimeout);
});

watch(
  () => props.isOpen,
  async (val) => {
    if (val) {
      document.body.style.overflow = 'hidden';
      await nextTick();
      drawerRef.value?.focus();
      setTimeout(() => inputRef.value?.focus(), 80);
    } else {
      document.body.style.overflow = '';
      query.value = '';
      results.value = [];
      isSearching.value = false;
    }
  }
);
</script>

<style scoped>
/* ── Overlay ────────────────────────────────────────────── */
.search-drawer-overlay {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.45);
  z-index: 1055;
}

/* ── Drawer panel ───────────────────────────────────────── */
.search-drawer {
  position: fixed;
  top: 0;
  right: 0;
  width: 420px;
  max-width: 100%;
  height: 100%;
  background: #fff;
  z-index: 1056;
  display: flex;
  flex-direction: column;
  box-shadow: -4px 0 28px rgba(0, 0, 0, 0.14);
  outline: none;
}

/* ── Transitions ────────────────────────────────────────── */
.overlay-fade-enter-active,
.overlay-fade-leave-active {
  transition: opacity 0.25s ease;
}
.overlay-fade-enter-from,
.overlay-fade-leave-to {
  opacity: 0;
}

.drawer-slide-enter-active,
.drawer-slide-leave-active {
  transition: transform 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.drawer-slide-enter-from,
.drawer-slide-leave-to {
  transform: translateX(100%);
}

/* ── Header ─────────────────────────────────────────────── */
.search-drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 1.25rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.search-drawer-title {
  font-size: 1.1rem;
  font-weight: 600;
  margin: 0;
  color: #1a1a1a;
}

.search-drawer-close {
  background: transparent;
  border: none;
  cursor: pointer;
  font-size: 1rem;
  color: #666;
  padding: 0.35rem;
  line-height: 1;
  border-radius: 4px;
  transition: background 0.15s, color 0.15s;
}
.search-drawer-close:hover {
  background: #f5f5f5;
  color: #1a1a1a;
}

/* ── Input wrap ─────────────────────────────────────────── */
.search-drawer-input-wrap {
  padding: 1rem 1.5rem;
  border-bottom: 1px solid #f0f0f0;
  flex-shrink: 0;
}

.search-input-inner {
  display: flex;
  align-items: center;
  background: #f7f7f7;
  border: 1.5px solid #ececec;
  border-radius: 10px;
  padding: 0 0.75rem;
  transition: border-color 0.2s, background 0.2s;
}

.search-input-inner:focus-within {
  border-color: #1a1a1a;
  background: #fff;
  box-shadow: 0 0 0 3px rgba(26, 26, 26, 0.07);
}

.search-input-icon {
  color: #999;
  font-size: 0.85rem;
  flex-shrink: 0;
  margin-right: 0.5rem;
}

.search-drawer-input {
  flex: 1;
  border: none;
  background: transparent;
  padding: 0.75rem 0;
  font-size: 0.95rem;
  color: #1a1a1a;
  outline: none;
}

.search-drawer-input::placeholder {
  color: #aaa;
}

.search-clear-btn {
  background: transparent;
  border: none;
  cursor: pointer;
  color: #bbb;
  font-size: 0.9rem;
  padding: 0.2rem;
  line-height: 1;
  transition: color 0.15s;
  flex-shrink: 0;
}
.search-clear-btn:hover {
  color: #555;
}

/* ── Body (scrollable) ──────────────────────────────────── */
.search-drawer-body {
  flex: 1;
  overflow-y: auto;
  padding: 0.5rem 0;
}

/* ── Results header ─────────────────────────────────────── */
.search-results-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0.6rem 1.5rem 0.4rem;
}

.search-results-count {
  font-size: 0.75rem;
  color: #999;
  font-weight: 500;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.search-see-all-btn {
  background: transparent;
  border: none;
  font-size: 0.78rem;
  font-weight: 600;
  color: #1a1a1a;
  cursor: pointer;
  padding: 0.2rem 0;
  transition: opacity 0.15s;
}
.search-see-all-btn:hover {
  opacity: 0.7;
}

/* ── Result item ────────────────────────────────────────── */
.search-result-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.5rem;
  border-bottom: 1px solid #f5f5f5;
  text-decoration: none !important;
  cursor: pointer;
  transition: background 0.15s;
  color: inherit;
}
.search-result-item:last-child {
  border-bottom: none;
}
.search-result-item:hover {
  background: #fafafa;
}

.search-result-img-wrap {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  overflow: hidden;
  background: #f5f5f5;
  display: flex;
  align-items: center;
  justify-content: center;
}

.search-result-img {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.search-result-img-placeholder {
  color: #ccc;
  font-size: 1.2rem;
}

.search-result-info {
  flex: 1;
  min-width: 0;
}

.search-result-name {
  font-size: 0.875rem;
  font-weight: 500;
  color: #1a1a1a;
  margin: 0 0 0.15rem;
  line-height: 1.35;
  display: -webkit-box;
  -webkit-line-clamp: 2;
  line-clamp: 2;
  -webkit-box-orient: vertical;
  overflow: hidden;
}

.search-result-brand {
  font-size: 0.75rem;
  color: #999;
  margin: 0 0 0.3rem;
}

.search-result-price-row {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  flex-wrap: wrap;
}

.search-result-original-price {
  font-size: 0.75rem;
  color: #bbb;
  text-decoration: line-through;
}

.search-result-price {
  font-size: 0.875rem;
  font-weight: 700;
  color: #1a1a1a;
}

.search-result-badge {
  font-size: 0.67rem;
  background: #e8f5e9;
  color: #2e7d32;
  padding: 1px 5px;
  border-radius: 3px;
  font-weight: 600;
}

.search-result-arrow {
  color: #ddd;
  font-size: 0.75rem;
  flex-shrink: 0;
}

/* ── Empty / initial states ─────────────────────────────── */
.search-empty-state,
.search-initial-state {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 3rem 2rem;
  text-align: center;
  flex: 1;
}

.search-empty-icon,
.search-initial-icon {
  font-size: 2.5rem;
  color: #e0e0e0;
  margin-bottom: 1rem;
}

.search-empty-title {
  font-size: 1rem;
  font-weight: 600;
  color: #1a1a1a;
  margin: 0 0 0.35rem;
}

.search-empty-sub {
  font-size: 0.85rem;
  color: #888;
  margin: 0;
}

.search-initial-text {
  font-size: 0.9rem;
  color: #aaa;
  margin: 0;
}

/* ── Skeleton ───────────────────────────────────────────── */
.search-skeleton-item {
  display: flex;
  align-items: center;
  gap: 0.875rem;
  padding: 0.875rem 1.5rem;
  border-bottom: 1px solid #f5f5f5;
}

.search-skeleton-img {
  flex-shrink: 0;
  width: 60px;
  height: 60px;
  border-radius: 8px;
  background: #f0f0f0;
  animation: shimmer 1.4s infinite;
}

.search-skeleton-text {
  flex: 1;
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.search-skeleton-name {
  height: 14px;
  width: 70%;
  background: #f0f0f0;
  border-radius: 4px;
  animation: shimmer 1.4s infinite;
}

.search-skeleton-price {
  height: 12px;
  width: 35%;
  background: #f0f0f0;
  border-radius: 4px;
  animation: shimmer 1.4s infinite 0.2s;
}

@keyframes shimmer {
  0%, 100% { opacity: 0.55; }
  50% { opacity: 1; }
}

/* ── Footer ─────────────────────────────────────────────── */
.search-drawer-footer {
  border-top: 1px solid #f0f0f0;
  padding: 1rem 1.5rem;
  flex-shrink: 0;
  background: #fff;
}

.search-full-results-btn {
  display: block;
  width: 100%;
  background-color: #1a1a1a;
  color: #fff;
  border: none;
  padding: 0.78rem 1rem;
  border-radius: 8px;
  font-weight: 600;
  font-size: 0.875rem;
  text-align: center;
  cursor: pointer;
  transition: background 0.2s, transform 0.1s;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.search-full-results-btn:hover {
  background-color: #333;
}
.search-full-results-btn:active {
  transform: scale(0.98);
}

/* ── Responsive ─────────────────────────────────────────── */
@media (max-width: 768px) {
  .search-drawer {
    width: 100%;
  }
}
</style>
